import React, { useState, useEffect, useCallback } from "react";
import "../../assets/css/CompanyProfile.css";
import PrimaryButton from "../../components/common/PrimaryButton";
import ReusableTextField from "../../components/common/ReusableTextField";
import UserService from "../../services/UserService";
import TeamMemberService, {
  mapApiMemberToUi,
  roleToUserType,
} from "../../services/TeamMemberService";
import UserSessionManager from "../../utils/UserSessionManager";
import DeleteSweetAlert from "../../utils/DeleteSweetAlert";
import { FaUpload, FaSearch, FaInfoCircle, FaEllipsisH } from "react-icons/fa";
import TeamMemberModal from "./TeamMemberModal";
import { toast } from "react-toastify";

const AVATAR_COLORS = ["blue", "purple", "blue", "purple"];

function getInitials(firstName, lastName) {
  return `${(firstName?.[0] || "").toUpperCase()}${(lastName?.[0] || "").toUpperCase()}`;
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function pickProfileField(details, ...keys) {
  for (const key of keys) {
    const value = details?.[key];
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return "";
}

function applyCompanyDetails(details, setters) {
  const { setCompanyName, setCompanyTagline, setLogoPreview, setCompanyId } = setters;
  setCompanyName(pickProfileField(details, "companyName", "CompanyName"));
  setCompanyTagline(
    pickProfileField(
      details,
      "businessDetails",
      "BusinessDetails",
      "companyTagline",
      "CompanyTagline"
    )
  );
  const logo = pickProfileField(details, "companyLogo", "CompanyLogo");
  if (logo) setLogoPreview(logo);

  const id = pickProfileField(details, "companyId", "CompanyId");
  if (id && setCompanyId) {
    setCompanyId(id);
    UserSessionManager.updateSession({ companyId: id });
  }
}

export default function CompanyProfile() {
  const sessionUser = UserSessionManager.getUserInfo();
  const isReadOnly = UserSessionManager.isDeveloper();
  const [companyId, setCompanyId] = useState(sessionUser?.companyId || "");
  const [companyName, setCompanyName] = useState(sessionUser?.companyName || "");
  const [companyTagline, setCompanyTagline] = useState("");
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [members, setMembers] = useState([]);
  const [totalMembers, setTotalMembers] = useState(0);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [saving, setSaving] = useState(false);
  const [memberSubmitting, setMemberSubmitting] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);

  const loadCompanyProfile = useCallback(async () => {
    try {
      const response = await UserService.fetchUserProfile();
      if (response.data?.success && response.data.data) {
        applyCompanyDetails(response.data.data, {
          setCompanyName,
          setCompanyTagline,
          setLogoPreview,
          setCompanyId,
        });
      }
    } catch (error) {
      console.error("Error fetching company profile:", error);
    }
  }, []);

  const loadTeamMembers = useCallback(async (search = "", activeCompanyId = companyId) => {
    if (!activeCompanyId) return;

    setLoadingMembers(true);
    try {
      const response = await TeamMemberService.fetchTeamMembersList({
        companyId: activeCompanyId,
        pageNumber: 1,
        pageSize: 5,
        search,
      });

      if (response.data?.success) {
        const data = response.data.data || {};
        const items = Array.isArray(data.items) ? data.items : [];
        setMembers(items.map(mapApiMemberToUi).filter(Boolean));
        setTotalMembers(data.totalCount ?? items.length);
      } else {
        toast.error(response.data?.message || "Failed to load team members.");
      }
    } catch (error) {
      const apiMessage =
        error.response?.data?.message || error.message || "Please try again.";
      toast.error(`Failed to load team members. ${apiMessage}`);
    } finally {
      setLoadingMembers(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadCompanyProfile();
  }, [loadCompanyProfile]);

  useEffect(() => {
    if (!companyId) return;
    const timer = setTimeout(() => {
      loadTeamMembers(searchQuery, companyId);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery, companyId, loadTeamMembers]);

  const handleLogoUpload = (e) => {
    if (isReadOnly) return;
    if (!e.target.files?.[0]) return;
    const file = e.target.files[0];
    const allowed = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowed.includes(file.type)) {
      toast.error("Only JPG, JPEG, PNG or WEBP images are allowed.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB.");
      return;
    }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleSaveCompanyProfile = async () => {
    if (isReadOnly) return;
    if (!companyName.trim()) {
      toast.error("Company name is required.");
      return;
    }

    setSaving(true);
    try {
      const response = await UserService.updateUserCompany(
        {
          companyName: companyName.trim(),
          companyTagline: companyTagline.trim(),
        },
        logoFile
      );

      if (response.data?.success) {
        UserSessionManager.updateSession({ companyName: companyName.trim() });
        setLogoFile(null);
        await loadCompanyProfile();
        toast.success("Company profile updated successfully!");
      } else {
        toast.error(response.data?.message || "Failed to update company profile.");
      }
    } catch (error) {
      const apiMessage =
        error.response?.data?.message || error.message || "Please try again.";
      toast.error(`Failed to update company profile. ${apiMessage}`);
    } finally {
      setSaving(false);
    }
  };

  const handleAddMember = async (formData) => {
    if (!companyId) {
      toast.error("Company ID is missing. Please refresh and try again.");
      return;
    }

    setMemberSubmitting(true);
    try {
      const payload = {
        companyId,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        whatsAppNo: formData.whatsAppNo,
        userType: roleToUserType(formData.role),
        status: true,
      };

      const response = await TeamMemberService.addTeamMember(payload);
      if (response.data?.success) {
        toast.success("Team member added successfully!");
        setShowAddModal(false);
        await loadTeamMembers(searchQuery, companyId);
      } else {
        toast.error(response.data?.message || "Failed to add team member.");
      }
    } catch (error) {
      const apiMessage =
        error.response?.data?.message || error.message || "Please try again.";
      toast.error(`Failed to add team member. ${apiMessage}`);
    } finally {
      setMemberSubmitting(false);
    }
  };

  const handleEditMember = async (formData) => {
    if (!editingMember?.id) return;

    setMemberSubmitting(true);
    try {
      const payload = {
        id: editingMember.id,
        firstName: formData.firstName,
        lastName: formData.lastName,
        userType: roleToUserType(formData.role),
        status: formData.status === "Active",
      };

      const response = await TeamMemberService.updateTeamMember(payload);
      if (response.data?.success) {
        toast.success("Team member updated successfully!");
        setShowEditModal(false);
        setEditingMember(null);
        await loadTeamMembers(searchQuery, companyId);
      } else {
        toast.error(response.data?.message || "Failed to update team member.");
      }
    } catch (error) {
      const apiMessage =
        error.response?.data?.message || error.message || "Please try again.";
      toast.error(`Failed to update team member. ${apiMessage}`);
    } finally {
      setMemberSubmitting(false);
    }
  };

  const handleDeleteMember = async () => {
    if (!editingMember?.id) return;

    const confirmed = await DeleteSweetAlert({
      title: "Delete team member?",
      text: `${editingMember.firstName} ${editingMember.lastName} will be removed from your organization.`,
      confirmButtonText: "Delete",
    });
    if (!confirmed) return;

    setMemberSubmitting(true);
    try {
      const response = await TeamMemberService.deleteTeamMember(editingMember.id);
      if (response.data?.success) {
        toast.success("Team member deleted successfully!");
        setShowEditModal(false);
        setEditingMember(null);
        await loadTeamMembers(searchQuery, companyId);
      } else {
        toast.error(response.data?.message || "Failed to delete team member.");
      }
    } catch (error) {
      const apiMessage =
        error.response?.data?.message || error.message || "Please try again.";
      toast.error(`Failed to delete team member. ${apiMessage}`);
    } finally {
      setMemberSubmitting(false);
    }
  };

  const openEditModal = (member) => {
    if (isReadOnly) return;
    setEditingMember(member);
    setShowEditModal(true);
  };

  return (
    <div className={`company-profile${isReadOnly ? " company-profile-readonly" : ""}`}>
      {/* Company Information */}
      <div className="company-section-card">
        <div className="company-section-header">
          <h2 className="company-section-title">Company Information</h2>
          <p className="company-section-subtitle">
            Manage your organization profile details.
          </p>
        </div>

        <div className="company-info-layout">
          <div className="company-logo-upload">
            {isReadOnly ? (
              <div className="company-logo-box company-logo-box-readonly">
                {logoPreview ? (
                  <img src={logoPreview} alt="Company logo" className="preview" />
                ) : (
                  <>
                    <FaUpload className="company-logo-icon" />
                    <span className="company-logo-label">Company Logo</span>
                  </>
                )}
              </div>
            ) : (
              <>
                <label htmlFor="companyLogoInput" className="company-logo-box">
                  {logoPreview ? (
                    <img src={logoPreview} alt="Company logo" className="preview" />
                  ) : (
                    <>
                      <FaUpload className="company-logo-icon" />
                      <span className="company-logo-label">Upload Logo</span>
                    </>
                  )}
                </label>
                <input
                  id="companyLogoInput"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleLogoUpload}
                  style={{ display: "none" }}
                />
                <label htmlFor="companyLogoInput" className="company-logo-link">
                  Upload logo
                </label>
              </>
            )}
          </div>

          <div className="company-info-fields">
            <div className="company-name-row">
              <ReusableTextField
                name="companyName"
                label="Company Name"
                required
                placeholder="Enter company name"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                helpText="This name is stored in the Admin Database and represents your organization within the platform."
                disabled={isReadOnly}
              />
              {!isReadOnly && (
                <PrimaryButton onClick={handleSaveCompanyProfile} disabled={saving}>
                  {saving ? "Saving..." : "Save"}
                </PrimaryButton>
              )}
            </div>

            <ReusableTextField
              name="companyTagline"
              label="Company Tagline"
              placeholder="e.g. Let's grow together.."
              value={companyTagline}
              onChange={(e) => setCompanyTagline(e.target.value)}
              helpText="Optional — a short phrase displayed alongside your company name."
              disabled={isReadOnly}
            />
          </div>
        </div>
      </div>

      {/* Team Members */}
      <div className="company-section-card">
        <div className="team-members-header">
          <div className="company-section-header" style={{ marginBottom: 0 }}>
            <h2 className="company-section-title">Team Members</h2>
            <p className="company-section-subtitle">
              {totalMembers} member{totalMembers !== 1 ? "s" : ""} in your organization
            </p>
          </div>
          {!isReadOnly && (
            <PrimaryButton
              style={{ padding: "8px 16px", fontSize: "0.875rem" }}
              onClick={() => {
                if (!companyId) {
                  toast.error("Company ID is missing. Please save your company profile first.");
                  return;
                }
                setShowAddModal(true);
              }}
            >
              + Add Member
            </PrimaryButton>
          )}
        </div>

        <div className="team-search-wrap">
          <FaSearch className="team-search-icon" />
          <input
            type="text"
            className="team-search-input"
            placeholder="Search members..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="table-responsive">
          <table className="team-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Date Added</th>
                {!isReadOnly && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {loadingMembers ? (
                <tr>
                  <td colSpan={isReadOnly ? 5 : 6} className="text-center text-muted py-4">
                    Loading members...
                  </td>
                </tr>
              ) : members.length === 0 ? (
                <tr>
                  <td colSpan={isReadOnly ? 5 : 6} className="text-center text-muted py-4">
                    No members found.
                  </td>
                </tr>
              ) : (
                members.map((member, idx) => (
                  <tr key={member.id}>
                    <td>
                      <div className="member-name-cell">
                        <span
                          className={`member-avatar ${AVATAR_COLORS[idx % AVATAR_COLORS.length]}`}
                        >
                          {getInitials(member.firstName, member.lastName)}
                        </span>
                        <span>
                          {member.firstName} {member.lastName}
                        </span>
                      </div>
                    </td>
                    <td>{member.email}</td>
                    <td>
                      <span
                        className={`role-badge ${
                          member.role === "Admin" ? "admin" : "developer"
                        }`}
                      >
                        {member.role}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`status-badge ${
                          member.status === "Active" ? "active" : "inactive"
                        }`}
                      >
                        <span className="status-dot" />
                        {member.status}
                      </span>
                    </td>
                    <td>{formatDate(member.dateAdded)}</td>
                    {!isReadOnly && (
                      <td>
                        <button
                          type="button"
                          className="team-actions-btn"
                          aria-label="Edit member"
                          onClick={() => openEditModal(member)}
                        >
                          <FaEllipsisH />
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="team-footer-note">
          <FaInfoCircle />
          <span>
            New members receive a temporary password and must change it on first login.
          </span>
        </div>
      </div>

      {!isReadOnly && (
        <>
          <TeamMemberModal
            show={showAddModal}
            mode="add"
            onClose={() => setShowAddModal(false)}
            onSave={handleAddMember}
            isSubmitting={memberSubmitting}
          />

          <TeamMemberModal
            show={showEditModal}
            mode="edit"
            member={editingMember}
            onClose={() => {
              setShowEditModal(false);
              setEditingMember(null);
            }}
            onSave={handleEditMember}
            onDelete={handleDeleteMember}
            isSubmitting={memberSubmitting}
          />
        </>
      )}
    </div>
  );
}
